import { parseProjectQueryRequest, type ProjectQueryRequest, type QueryFieldRef, type QueryPath, type QueryPredicate } from "@secretaa/contracts-data";
import { parseMoney, separatorsOf } from "@secretaa/ui";
import { activeFields, type MCollection, type MField } from "../data/manifest-view.ts";

export type FilterOp = "eq" | "ne" | "lt" | "lte" | "gt" | "gte" | "contains" | "starts_with" | "is_null" | "in";
export type DraftPredicate =
  | { kind: "group"; op: "and" | "or"; negate: boolean; args: DraftPredicate[] }
  | { kind: "exists"; alias: string; negate: boolean; where: DraftPredicate }
  | { kind: "condition"; ref: QueryFieldRef; op: FilterOp; values: string[]; locale: string };
export interface QueryDraft {
  root: string; paths: QueryPath[]; select: QueryFieldRef[]; where: DraftPredicate;
  scope: string; sort: ProjectQueryRequest["sort"]; limit: number; rootIds: string[];
}
export interface AliasView { alias: string; collection: MCollection; depth: number; scalar: boolean }
export const blankGroup = (): DraftPredicate => ({ kind: "group", op: "and", negate: false, args: [] });
export function newDraft(c: MCollection): QueryDraft {
  return { root: c.id, paths: [], select: activeFields(c).filter(f => f.type !== "relation").slice(0, 4).map(f => ({ pathAlias: "root", fieldId: f.id })),
    where: blankGroup(), scope: "", sort: { by: { system: "createdAt" }, dir: "desc" }, limit: 25, rootIds: [] };
}
export function aliasesFor(collections: readonly MCollection[], draft: Pick<QueryDraft, "root" | "paths">): AliasView[] {
  const root = collections.find(c => c.id === draft.root && c.status === "active");
  if (!root) return [];
  const aliases: AliasView[] = [{ alias: "root", collection: root, depth: 0, scalar: true }];
  // UI appends parents before children; no query interpretation or authorization lives here.
  for (const p of draft.paths) {
    const parent = aliases.find(a => a.alias === p.fromAlias);
    const owner = p.direction === "forward" ? parent?.collection : collections.find(c => c.fields.some(f => f.id === p.relationFieldId));
    const field = owner?.fields.find(f => f.id === p.relationFieldId);
    const target = field?.type === "relation" ? (field.config as { targetCollectionId: string }).targetCollectionId : null;
    const c = p.direction === "forward" ? collections.find(c => c.id === target) : owner;
    if (parent && c?.status === "active" && field?.status === "active") aliases.push({ alias: p.alias, collection: c, depth: parent.depth + 1, scalar: parent.scalar && p.direction === "forward" });
  }
  return aliases;
}
export function relationChoices(collections: readonly MCollection[], aliases: AliasView[], paths: QueryPath[]) {
  return aliases.filter(a => a.depth < 2).flatMap(a => collections.filter(c => c.status === "active").flatMap(c => activeFields(c).flatMap(f => {
    if (f.type !== "relation") return [];
    const target = (f.config as { targetCollectionId: string }).targetCollectionId;
    return (["forward", "reverse"] as const).flatMap(direction => {
      if ((direction === "forward" && c.id !== a.collection.id) || (direction === "reverse" && (target !== a.collection.id || !f.indexed))) return [];
      if (paths.some(p => p.fromAlias === a.alias && p.relationFieldId === f.id && p.direction === direction)) return [];
      const destination = direction === "forward" ? collections.find(x => x.id === target && x.status === "active") : c;
      return destination ? [{ fromAlias: a.alias, relationFieldId: f.id, direction, parent: a, field: f, destination }] : [];
    });
  })));
}
export const refKey = (ref: QueryFieldRef) => `${ref.pathAlias}:${ref.fieldId}`;
export function fieldsAt(aliases: AliasView[], paths: QueryPath[], current: string) {
  const reachable = (alias: string): boolean => alias === current || paths.some(p => p.alias === alias && p.direction === "forward" && reachable(p.fromAlias));
  return aliases.filter(a => reachable(a.alias)).flatMap(a => activeFields(a.collection).filter(f => f.indexed && f.type !== "relation").map(field => ({ alias: a, field, ref: { pathAlias: a.alias, fieldId: field.id } })));
}
export function opsFor(field: MField): FilterOp[] {
  const base: FilterOp[] = ["eq", "ne", "in", "is_null"];
  if (["text", "number", "money", "date"].includes(field.type)) base.push("lt", "lte", "gt", "gte");
  if (field.type === "text") base.push("contains", "starts_with");
  return base;
}
export function nextMonth(reference: string): [string, string] {
  const d = new Date(`${reference}T00:00:00Z`);
  return [new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1)).toISOString().slice(0, 10), new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 2, 1)).toISOString().slice(0, 10)];
}
export class DraftError extends Error {}
function predicate(d: DraftPredicate, aliases: AliasView[], top = false): QueryPredicate | null {
  if (d.kind === "group") {
    if (!d.args.length) { if (top && !d.negate) return null; throw new DraftError(); }
    const args = d.args.map(a => predicate(a, aliases)!);
    const group: QueryPredicate = { op: d.op, args };
    return d.negate ? { op: "not", arg: group } : group;
  }
  if (d.kind === "exists") {
    const exists: QueryPredicate = { op: "exists", pathAlias: d.alias, where: predicate(d.where, aliases)! };
    return d.negate ? { op: "not", arg: exists } : exists;
  }
  const f = aliases.find(a => a.alias === d.ref.pathAlias)?.collection.fields.find(f => f.id === d.ref.fieldId);
  if (!f) throw new DraftError();
  if (d.op === "is_null") return { op: "is_null", arg: d.ref };
  const literals = d.values.map(v => {
    if (v === "") throw new DraftError();
    if (f.type === "money") {
      const cfg = f.config as { currency: string; minorUnitScale: number };
      const money = parseMoney(v, cfg.minorUnitScale, separatorsOf(d.locale));
      if (money === null) throw new DraftError();
      return { money, currency: cfg.currency };
    }
    if (f.type === "number") {
      const number = v.replace(separatorsOf(d.locale).decimal, ".");
      if (!/^-?\d+(?:\.\d+)?$/.test(number)) throw new DraftError();
      return { number };
    }
    if (f.type === "date") return { date: v };
    if (f.type === "bool") { if (v !== "true" && v !== "false") throw new DraftError(); return v === "true"; }
    return v;
  });
  if (!literals.length) throw new DraftError();
  return { op: d.op, left: d.ref, right: { literal: d.op === "in" ? literals : literals[0]! } };
}
export function requestFromDraft(draft: QueryDraft, collections: readonly MCollection[], manifestHash: string): ProjectQueryRequest {
  return parseProjectQueryRequest({ requestVersion: 1, manifestHash, rootCollectionId: draft.root, paths: draft.paths,
    select: draft.select.map((s, i) => ({ ...s, as: `f${i}` })), where: predicate(draft.where, aliasesFor(collections, draft), true),
    sort: draft.sort, page: { limit: draft.limit }, ...(draft.scope ? { scopeRootId: draft.scope } : {}), ...(draft.rootIds.length ? { rootRecordIds: draft.rootIds } : {}) });
}
